/**
 * Pure-function builders for `sync.decks.share()` / `sync.decks.unshare()`.
 *
 * Parallels `quiz/sharing/operations.ts`, but operates on `SyncDeckMeta`
 * (the `deckProperties` record entity) and carries per-share toggle
 * choices (`shareOptions`). The `sync` schema wraps these with
 * `setDeckProperty(next)` — which auto-stamps `updatedAtISO` so the push
 * fires — keeping the logic here pure + testable without the sync engine.
 *
 * Unlike quiz (where a share is purely idempotent), a deck re-share may
 * legitimately change the toggle choices, so a re-share with DIFFERENT
 * `shareOptions` is NOT a no-op: it reuses the existing code but refreshes
 * the options. Only a re-share with identical options short-circuits.
 */

import { mintShareCode as defaultMintShareCode } from '../../sharing/code';
import type {
  DeckShareOptions,
  SyncDeckMeta,
} from '../../sync-engine/schema/shared-types';

export interface BuildDeckShareUpdateResult {
  /** Next meta to write, or `null` when no write is needed (missing /
   *  tombstoned deck, or an idempotent re-share with unchanged options). */
  next: SyncDeckMeta | null;
  /** The shareCode now active on the deck, or `null` when the deck can't
   *  be shared (missing / tombstoned). */
  code: string | null;
}

export interface BuildDeckShareUpdateOptions {
  /** Override the timestamp stamped on `linkSharedAtISO` for a fresh
   *  share. Defaults to `new Date().toISOString()`. */
  now?: string;
  /** Override the share-code mint function (tests inject a deterministic
   *  generator). Defaults to the cryptographic `mintShareCode()`. */
  mintCode?: () => string;
}

function sameShareOptions(a: DeckShareOptions | undefined, b: DeckShareOptions): boolean {
  const na = a ?? {};
  return (
    !!na.sharePdf === !!b.sharePdf &&
    !!na.shareAiHelps === !!b.shareAiHelps &&
    !!na.shareNotes === !!b.shareNotes &&
    JSON.stringify(na.notesSnapshot ?? null) ===
      JSON.stringify(b.notesSnapshot ?? null) &&
    // A second note edited since the last share is a re-share worth writing:
    // the snapshot only refreshes when the row changes.
    JSON.stringify(na.notesSnapshots ?? null) ===
      JSON.stringify(b.notesSnapshots ?? null) &&
    JSON.stringify(na.liveNoteIds ?? null) === JSON.stringify(b.liveNoteIds ?? null) &&
    // A note too big for the row, edited since the last share: the row has to
    // change so the owner's UserDo re-freezes the copy it serves.
    (na.liveNotesRev ?? null) === (b.liveNotesRev ?? null)
  );
}

/**
 * Compute the next meta for `sync.decks.share(...)`.
 *
 * Cases:
 *   - missing / tombstoned deck → `{ next: null, code: null }`
 *   - already shared, same opts → `{ next: null, code: existingCode }` (no-op)
 *   - already shared, new opts  → `{ next: stampedMeta, code: existingCode }`
 *   - fresh share               → `{ next: stampedMeta, code: newCode }`
 */
export function buildDeckShareUpdate(
  meta: SyncDeckMeta | undefined,
  shareOptions: DeckShareOptions,
  opts: BuildDeckShareUpdateOptions = {},
): BuildDeckShareUpdateResult {
  if (!meta || meta.deletedTimeISO) return { next: null, code: null };

  const alreadyShared = !!(meta.shareCode && meta.linkSharedAtISO);
  if (alreadyShared && sameShareOptions(meta.shareOptions, shareOptions)) {
    return { next: null, code: meta.shareCode ?? null };
  }

  const mintCode = opts.mintCode ?? defaultMintShareCode;
  const now = opts.now ?? new Date().toISOString();
  const code = meta.shareCode ?? mintCode();
  const next: SyncDeckMeta = {
    ...meta,
    shareCode: code,
    linkSharedAtISO: meta.linkSharedAtISO ?? now,
    shareOptions,
  };
  return { next, code };
}

/**
 * Compute the next meta for `sync.decks.unshare(...)`. Returns `null` when
 * there's nothing to clear (missing meta or not currently link-shared).
 */
export function buildDeckUnshareUpdate(
  meta: SyncDeckMeta | undefined,
): SyncDeckMeta | null {
  if (!meta) return null;
  if (!meta.shareCode && !meta.linkSharedAtISO && !meta.shareOptions) return null;
  const next: SyncDeckMeta = { ...meta };
  delete next.shareCode;
  delete next.linkSharedAtISO;
  delete next.shareOptions;
  return next;
}
